// How a list page picks the preview row when the activated row is missing from the refreshed list.
export enum ActivatedRowReselectEnum {
  // Nearest remaining row: the one after the removed rows, otherwise the one before them.
  NEIGHBOR = 'NEIGHBOR',
  FIRST_ROW = 'FIRST_ROW',
  NONE = 'NONE',
}

export function getReselectedActivatedRow<T>(
  mode: ActivatedRowReselectEnum,
  previousList: T[],
  nextList: T[],
  activatedRow: T,
  getId: (row: T) => number | string,
  noneFallback: T | null = null
): T | null {
  switch (mode) {
    case ActivatedRowReselectEnum.NEIGHBOR:
      return getNeighborRowAfterRefresh(previousList, nextList, activatedRow, getId);
    case ActivatedRowReselectEnum.FIRST_ROW:
      return nextList && nextList.length ? nextList[0] : null;
    default:
      return noneFallback;
  }
}

export function getNeighborRowAfterRefresh<T>(
  previousList: T[],
  nextList: T[],
  activatedRow: T,
  getId: (row: T) => number | string
): T | null {
  if (!nextList || !nextList.length) { return null; }

  // Neighbors are taken by the previous (displayed) order: rows with equal sort values may come back reordered.
  const previous = previousList || [];
  const activatedId = getId(activatedRow);
  const activatedIndex = previous.findIndex(row => getId(row) === activatedId);
  const findInNextList = (row: T): T | undefined => nextList.find(nextRow => getId(nextRow) === getId(row));

  for (let i = activatedIndex + 1; i < previous.length; i++) {
    const nextRow = findInNextList(previous[i]);
    if (nextRow) { return nextRow; }
  }

  const previousIds = previous.map(getId);
  const rowFromNextPage = nextList.find(row => previousIds.indexOf(getId(row)) === -1);
  if (rowFromNextPage) { return rowFromNextPage; }

  for (let i = activatedIndex - 1; i >= 0; i--) {
    const previousRow = findInNextList(previous[i]);
    if (previousRow) { return previousRow; }
  }

  return nextList[0];
}
